import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { format, parseISO, startOfQuarter, endOfQuarter, subQuarters, addQuarters } from 'date-fns';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useMediaQuery } from 'react-responsive';
import {
  Box, Container, Typography, Paper, Grid, Button, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, TextField, Alert, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel,
  ToggleButtonGroup, ToggleButton, Tabs, Tab, Autocomplete,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AssessmentIcon from '@mui/icons-material/Assessment';
import EmailIcon from '@mui/icons-material/Email';

import { useUserAuth } from '../../context/UserAuthContext';
import Header from '../../components/Header/Header';
import Logo from '../../components/Logo/Logo';
import DoEmailDialog from './DoEmailDialog';
import StoreEmailDialog from './StoreEmailDialog';

function fmtDate(v) {
  if (!v) return '—';
  try { return format(typeof v === 'string' ? parseISO(v) : v, 'MMM d, yyyy'); }
  catch { return v; }
}

// Sort a row array by a { key, dir } pair. Compares numbers as numbers and
// strings case-insensitively. Nullish values sort last regardless of dir so
// blank rows don't dominate a descending view.
function sortRows(rows, { key, dir }) {
  if (!rows || rows.length === 0) return rows;
  const sign = dir === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => {
    const av = a?.[key];
    const bv = b?.[key];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'number' && typeof bv === 'number') return sign * (av - bv);
    return sign * String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
  });
}

// TableCell with a click-to-sort label. Toggles asc/desc when the active key
// is clicked; otherwise swaps to the new key with a sensible default dir.
function SortableCell({ label, sortKey, sort, setSort, defaultDir = 'asc', align }) {
  const active = sort.key === sortKey;
  const nextDir = active ? (sort.dir === 'asc' ? 'desc' : 'asc') : defaultDir;
  return (
    <TableCell align={align} sortDirection={active ? sort.dir : false}>
      <TableSortLabel
        active={active}
        direction={active ? sort.dir : defaultDir}
        onClick={() => setSort({ key: sortKey, dir: nextDir })}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );
}

function pctColor(v) {
  if (v == null) return 'text.primary';
  if (v >= 85) return '#2e7d32';
  if (v >= 75) return '#558b2f';
  if (v >= 65) return '#f57c00';
  return '#c62828';
}

// Build a list of recent quarters (current + prior N) for the picker.
function buildQuarterOptions(count = 8) {
  const now = new Date();
  const opts = [];
  for (let i = 0; i < count; i++) {
    const d = subQuarters(now, i);
    const q = Math.floor(d.getMonth() / 3) + 1;
    const y = d.getFullYear();
    opts.push({
      key: `Q${q} ${y}`,
      label: `Q${q} ${y}`,
      start: startOfQuarter(d),
      end: endOfQuarter(d),
    });
  }
  return opts;
}

function KpiCard({ label, value, sub, color }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant="h5" fontWeight={700} sx={{ color: color || 'text.primary', lineHeight: 1.2, mt: 0.5 }}>
        {value ?? '—'}
      </Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Paper>
  );
}

export default function FacilitiesScores() {
  const navigate = useNavigate();
  const { user: firebaseUser } = useUserAuth();
  const isTabletOrMobile = useMediaQuery({ maxWidth: 1224 });

  const quarterOptions = useMemo(() => buildQuarterOptions(8), []);
  const [mode, setMode] = useState('quarter'); // 'quarter' | 'range'
  const [quarterKey, setQuarterKey] = useState(quarterOptions[0].key);
  const [customStart, setCustomStart] = useState(quarterOptions[0].start);
  const [customEnd, setCustomEnd] = useState(quarterOptions[0].end);

  const [userInfo, setUserInfo] = useState(null);
  const [allowedStores, setAllowedStores] = useState([]); // [{ storeId, storeName }]
  const [selectedStoreIds, setSelectedStoreIds] = useState([]); // empty = "all allowed"
  const [profileLoading, setProfileLoading] = useState(true);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(0);

  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [storeEmailDialogOpen, setStoreEmailDialogOpen] = useState(false);

  // Per-tab sort state. Default sort mirrors the server's initial order
  // (store name asc, dirOps asc, incidence desc) so behavior is stable.
  const [storeSort, setStoreSort] = useState({ key: 'store', dir: 'asc' });
  const [doSort, setDoSort] = useState({ key: 'dirOps', dir: 'asc' });
  const [areaSort, setAreaSort] = useState({ key: 'incidence', dir: 'desc' });

  const perStoreSorted = useMemo(
    () => sortRows(data?.perStore || [], storeSort),
    [data?.perStore, storeSort]
  );
  const perDOSorted = useMemo(
    () => sortRows(data?.perDO || [], doSort),
    [data?.perDO, doSort]
  );
  const issuesSorted = useMemo(
    () => sortRows(data?.issuesByArea || [], areaSort),
    [data?.issuesByArea, areaSort]
  );

  const getToken = useCallback(async () => {
    if (!firebaseUser?.getIdToken) return null;
    return firebaseUser.getIdToken(true);
  }, [firebaseUser]);

  const activeRange = useMemo(() => {
    if (mode === 'quarter') {
      const q = quarterOptions.find((o) => o.key === quarterKey) || quarterOptions[0];
      return { start: q.start, end: q.end, label: q.label };
    }
    return { start: customStart, end: customEnd, label: `${fmtDate(customStart)} – ${fmtDate(customEnd)}` };
  }, [mode, quarterKey, quarterOptions, customStart, customEnd]);

  // Load user profile + allowed stores once. The backend enforces scope
  // regardless, but this drives what the filter UI shows.
  useEffect(() => {
    if (!firebaseUser?.email) return;
    (async () => {
      setProfileLoading(true);
      try {
        const token = await getToken();
        if (!token) return;
        const email = firebaseUser.email;
        const [infoResp, storesResp] = await Promise.all([
          axios.get(`/hd-api/user/${encodeURIComponent(email)}/info`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`/hd-api/user/stores/${encodeURIComponent(email)}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        setUserInfo(infoResp.data?.userInfo || null);
        setAllowedStores(storesResp.data?.stores || []);
      } catch (e) {
        setError('Failed to load user profile.');
      } finally {
        setProfileLoading(false);
      }
    })();
  }, [firebaseUser, getToken]);

  const load = useCallback(async () => {
    if (!activeRange.start || !activeRange.end) return;
    if (!firebaseUser?.email) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      if (!token) return;
      const params = new URLSearchParams({
        startDate: format(activeRange.start, 'yyyy-MM-dd'),
        endDate: format(activeRange.end, 'yyyy-MM-dd'),
        user: firebaseUser.email,
      });
      if (selectedStoreIds.length > 0) params.set('storeIds', selectedStoreIds.join(','));
      const { data: resp } = await axios.get(`/hd-api/facilities-review/scores?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(resp);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load score data.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [activeRange.start, activeRange.end, getToken, firebaseUser, selectedStoreIds]);

  useEffect(() => { if (!profileLoading) load(); }, [load, profileLoading]);

  function handleExport() {
    if (!data) return;
    setExporting(true);
    try {
      const perStoreRows = data.perStore.map((r) => ({
        store: r.store,
        submitted: r.submitted ? format(parseISO(r.submitted), 'yyyy-MM-dd') : '',
        missed: r.missed,
        scored: r.scored,
        percentage: r.percentage,
        potentialScore: r.potentialScore,
        do: r.dirOps,
      }));
      const perDORows = data.perDO.map((r) => ({
        dirOps: r.dirOps,
        missed: r.missed,
        scored: r.scored,
        potentialScore: r.potentialScore,
        percentage: r.percentage,
      }));
      const issueRows = data.issuesByArea.map((r) => ({
        area: r.area,
        item: r.item,
        incidence: r.incidence,
      }));

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(perStoreRows), 'stores');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(perDORows), 'dirOps');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(issueRows), 'issuesByArea');

      const rangeSlug = mode === 'quarter'
        ? quarterKey.replace(' ', '-')
        : `${format(customStart, 'yyyy-MM-dd')}_${format(customEnd, 'yyyy-MM-dd')}`;
      XLSX.writeFile(wb, `facilitiesScores-${rangeSlug}.xlsx`);
    } catch (e) {
      setError('Failed to export report.');
    } finally {
      setExporting(false);
    }
  }

  return (
    <div>
      <Header isTabletOrMobile={isTabletOrMobile} />
      <Container maxWidth="xl">
        <Logo isTabletOrMobile={isTabletOrMobile} />

        <Box sx={{ mt: 2 }}>
          <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 2 }}>
            <Box sx={{
              backgroundColor: 'white',
              borderLeft: (theme) => `4px solid ${theme.palette.secondary.main}`,
              px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2,
            }}>
              <Box sx={{
                backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.1),
                borderRadius: 2, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <AssessmentIcon sx={{ fontSize: 32, color: 'secondary.main' }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, fontFamily: 'Lato' }}>
                  Facilities Walkthrough - Scores
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {activeRange.label}
                  {data?.scope && (
                    <>
                      {' · '}
                      <Chip
                        size="small"
                        variant="outlined"
                        label={
                          data.scope.kind === 'store' ? 'Store'
                          : data.scope.kind === 'district' ? `District (${data.scope.allowedCount} stores)`
                          : `All stores (${data.scope.allowedCount})`
                        }
                        sx={{ ml: 0.5 }}
                      />
                    </>
                  )}
                </Typography>
              </Box>
              <Button variant="outlined" size="small" onClick={load} disabled={loading} startIcon={<RefreshIcon />}>
                Refresh
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setEmailDialogOpen(true)}
                startIcon={<EmailIcon />}
              >
                Send DO Emails
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setStoreEmailDialogOpen(true)}
                startIcon={<EmailIcon />}
              >
                Send Store Emails
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleExport}
                disabled={exporting || loading || !data}
                startIcon={exporting ? <CircularProgress size={14} /> : <FileDownloadIcon />}
              >
                Export XLSX
              </Button>
            </Box>
          </Paper>

          <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 2, p: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={mode}
                onChange={(_, v) => v && setMode(v)}
              >
                <ToggleButton value="quarter">Quarter</ToggleButton>
                <ToggleButton value="range">Date range</ToggleButton>
              </ToggleButtonGroup>
              {mode === 'quarter' && (
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Quarter</InputLabel>
                  <Select value={quarterKey} label="Quarter" onChange={(e) => setQuarterKey(e.target.value)}>
                    {quarterOptions.map((q) => (
                      <MenuItem key={q.key} value={q.key}>{q.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {mode === 'range' && (
                <>
                  <DatePicker
                    selected={customStart}
                    onChange={setCustomStart}
                    selectsStart
                    startDate={customStart}
                    endDate={customEnd}
                    dateFormat="MMM d, yyyy"
                    customInput={<TextField size="small" label="Start" sx={{ width: 150 }} autoComplete="off" />}
                  />
                  <DatePicker
                    selected={customEnd}
                    onChange={setCustomEnd}
                    selectsEnd
                    startDate={customStart}
                    endDate={customEnd}
                    minDate={customStart}
                    dateFormat="MMM d, yyyy"
                    customInput={<TextField size="small" label="End" sx={{ width: 150 }} autoComplete="off" />}
                  />
                </>
              )}
              {/* Store role sees only their own store; hide the picker.
                  DO/Basecamp get a multi-select filter of their allowed set. */}
              {userInfo?.role !== 'Store' && allowedStores.length > 1 && (
                <Autocomplete
                  multiple
                  size="small"
                  sx={{ minWidth: 320, flex: 1, maxWidth: 640 }}
                  options={allowedStores}
                  getOptionLabel={(o) => o.storeName || String(o.storeId)}
                  isOptionEqualToValue={(a, b) => Number(a.storeId) === Number(b.storeId)}
                  value={allowedStores.filter((s) => selectedStoreIds.includes(Number(s.storeId)))}
                  onChange={(_, next) => setSelectedStoreIds(next.map((s) => Number(s.storeId)))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={selectedStoreIds.length === 0 ? 'Stores (all)' : `Stores (${selectedStoreIds.length})`}
                      placeholder="Filter stores"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.length <= 3
                      ? value.map((option, index) => (
                          <Chip
                            size="small"
                            label={option.storeName}
                            {...getTagProps({ index })}
                            key={option.storeId}
                          />
                        ))
                      : [<Chip key="count" size="small" label={`${value.length} selected`} />]
                  }
                />
              )}
              {selectedStoreIds.length > 0 && (
                <Button size="small" onClick={() => setSelectedStoreIds([])}>Clear stores</Button>
              )}
            </Box>
          </Paper>

          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
          ) : data ? (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={4}>
                  <KpiCard
                    label="Walkthroughs in period"
                    value={data.summary.walkthroughCount.toLocaleString()}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <KpiCard
                    label="Avg pass %"
                    value={data.summary.avgPercentage != null ? `${data.summary.avgPercentage}%` : '—'}
                    color={pctColor(data.summary.avgPercentage)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <KpiCard
                    label="Missed items (period total)"
                    value={data.issuesByArea.reduce((s, r) => s + r.incidence, 0).toLocaleString()}
                  />
                </Grid>
              </Grid>

              <Paper variant="outlined">
                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
                  <Tab label={`By Store (${data.perStore.length})`} />
                  <Tab label={`By DO (${data.perDO.length})`} />
                  <Tab label={`Issues by Area (${data.issuesByArea.length})`} />
                </Tabs>

                {tab === 0 && (
                  <TableContainer sx={{ maxHeight: 640 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <SortableCell label="Store" sortKey="store" sort={storeSort} setSort={setStoreSort} />
                          <SortableCell label="Submitted" sortKey="submitted" sort={storeSort} setSort={setStoreSort} />
                          <SortableCell label="DO" sortKey="dirOps" sort={storeSort} setSort={setStoreSort} />
                          <SortableCell label="Missed" sortKey="missed" sort={storeSort} setSort={setStoreSort} defaultDir="desc" align="right" />
                          <SortableCell label="Scored" sortKey="scored" sort={storeSort} setSort={setStoreSort} defaultDir="desc" align="right" />
                          <SortableCell label="Potential" sortKey="potentialScore" sort={storeSort} setSort={setStoreSort} defaultDir="desc" align="right" />
                          <SortableCell label="%" sortKey="percentage" sort={storeSort} setSort={setStoreSort} defaultDir="asc" align="right" />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {perStoreSorted.map((r) => (
                          <TableRow
                            key={r.fwtId}
                            hover
                            onClick={() => r.fwtId && navigate(`/facilities-review/walkthrough/${r.fwtId}`, { state: { from: 'scores' } })}
                            sx={{ cursor: r.fwtId ? 'pointer' : 'default' }}
                          >
                            <TableCell>{r.store}</TableCell>
                            <TableCell>{fmtDate(r.submitted)}</TableCell>
                            <TableCell>{r.dirOps || '—'}</TableCell>
                            <TableCell align="right">{r.missed}</TableCell>
                            <TableCell align="right">{r.scored}</TableCell>
                            <TableCell align="right">{r.potentialScore}</TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight={600} sx={{ color: pctColor(r.percentage) }}>
                                {r.percentage != null ? `${r.percentage}%` : '—'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                        {data.perStore.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                              <Typography variant="body2" color="text.secondary">No walkthroughs in this period.</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {tab === 1 && (
                  <TableContainer sx={{ maxHeight: 640 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <SortableCell label="Director of Ops" sortKey="dirOps" sort={doSort} setSort={setDoSort} />
                          <SortableCell label="Missed" sortKey="missed" sort={doSort} setSort={setDoSort} defaultDir="desc" align="right" />
                          <SortableCell label="Scored" sortKey="scored" sort={doSort} setSort={setDoSort} defaultDir="desc" align="right" />
                          <SortableCell label="Potential" sortKey="potentialScore" sort={doSort} setSort={setDoSort} defaultDir="desc" align="right" />
                          <SortableCell label="%" sortKey="percentage" sort={doSort} setSort={setDoSort} defaultDir="asc" align="right" />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {perDOSorted.map((r) => (
                          <TableRow key={r.dirOps} hover>
                            <TableCell>{r.dirOps}</TableCell>
                            <TableCell align="right">{r.missed}</TableCell>
                            <TableCell align="right">{r.scored}</TableCell>
                            <TableCell align="right">{r.potentialScore}</TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight={600} sx={{ color: pctColor(r.percentage) }}>
                                {r.percentage != null ? `${r.percentage}%` : '—'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                        {data.perDO.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                              <Typography variant="body2" color="text.secondary">No DO data.</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {tab === 2 && (
                  <TableContainer sx={{ maxHeight: 640 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <SortableCell label="Area" sortKey="area" sort={areaSort} setSort={setAreaSort} />
                          <SortableCell label="Item" sortKey="item" sort={areaSort} setSort={setAreaSort} />
                          <SortableCell label="Incidence" sortKey="incidence" sort={areaSort} setSort={setAreaSort} defaultDir="desc" align="right" />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {issuesSorted.map((r, i) => (
                          <TableRow key={`${r.area}|${r.item}|${i}`} hover>
                            <TableCell>{r.area}</TableCell>
                            <TableCell>{r.item}</TableCell>
                            <TableCell align="right">{r.incidence.toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                        {data.issuesByArea.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                              <Typography variant="body2" color="text.secondary">No missed items in this period.</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </>
          ) : null}
        </Box>
      </Container>

      <DoEmailDialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        firebaseUser={firebaseUser}
        getToken={getToken}
      />
      <StoreEmailDialog
        open={storeEmailDialogOpen}
        onClose={() => setStoreEmailDialogOpen(false)}
        firebaseUser={firebaseUser}
        getToken={getToken}
      />
    </div>
  );
}
